import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { 
  BookOpen, 
  Sparkles, 
  Settings, 
  Volume2, 
  VolumeX,
  Play,
  Pause,
  Square,
  Save,
  Trash2,
  FileText,
  User,
  LogOut,
  Menu,
  ChevronDown,
  Loader2,
  Sun,
  Moon,
  Type,
  AlignLeft,
  Eye
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState("read");
  const [text, setText] = useState("");
  const [summarizedText, setSummarizedText] = useState("");
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [savedTexts, setSavedTexts] = useState([]);
  const [loadingTexts, setLoadingTexts] = useState(false);
  const [textTitle, setTextTitle] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // TTS State
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const speechRef = useRef(null);
  
  // Reading Profile State
  const [profile, setProfile] = useState({
    disorder_type: "general",
    font_size: 18,
    font_family: "Open Sans",
    line_height: 1.8,
    letter_spacing: 0.05,
    word_spacing: 0.1,
    background_color: "#F9F8F6",
    text_color: "#1A202C",
    reading_speed: 200,
    highlight_current_line: true,
    dyslexia_font_enabled: false
  });
  
  const [summarizationLevel, setSummarizationLevel] = useState("medium");

  // Fetch reading profile and saved texts on mount
  useEffect(() => {
    fetchProfile();
    fetchSavedTexts();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile/reading`, getAuthHeader());
      setProfile(response.data);
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const fetchSavedTexts = async () => {
    setLoadingTexts(true);
    try {
      const response = await axios.get(`${API}/texts`, getAuthHeader());
      setSavedTexts(response.data);
    } catch (error) {
      console.error("Error fetching texts:", error);
    } finally {
      setLoadingTexts(false);
    }
  };

  const updateProfile = async (updates) => {
    const newProfile = { ...profile, ...updates };
    setProfile(newProfile);
    
    try {
      await axios.put(`${API}/profile/reading`, { reading_profile: newProfile }, getAuthHeader());
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to save profile settings");
    }
  };

  const handleSummarize = async () => {
    if (!text.trim()) {
      toast.error("Please enter some text to summarize");
      return;
    }
    
    setIsSummarizing(true);
    try {
      const response = await axios.post(`${API}/summarize`, {
        text,
        summarization_level: summarizationLevel,
        disorder_type: profile.disorder_type !== "general" ? profile.disorder_type : null
      }, getAuthHeader());
      
      setSummarizedText(response.data.summarized_text);
      toast.success(`Text summarized! ${response.data.word_count_original} → ${response.data.word_count_summary} words`);
    } catch (error) {
      console.error("Summarization error:", error);
      toast.error(error.response?.data?.detail || "Failed to summarize text");
    } finally {
      setIsSummarizing(false);
    }
  };

  const handleSaveText = async () => {
    if (!text.trim()) {
      toast.error("Please enter some text to save");
      return;
    }
    
    const title = textTitle.trim() || `Text ${new Date().toLocaleDateString()}`;
    
    try {
      await axios.post(`${API}/texts`, {
        title,
        original_text: text,
        summarized_text: summarizedText || null
      }, getAuthHeader());
      
      toast.success("Text saved successfully!");
      setTextTitle("");
      fetchSavedTexts();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save text");
    }
  };

  const handleDeleteText = async (textId) => {
    try {
      await axios.delete(`${API}/texts/${textId}`, getAuthHeader());
      toast.success("Text deleted");
      fetchSavedTexts();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete text");
    }
  };

  const handleLoadText = (savedText) => {
    setText(savedText.original_text);
    setSummarizedText(savedText.summarized_text || "");
    setTextTitle(savedText.title);
    setActiveTab("read");
    toast.success("Text loaded");
  };

  // Text-to-Speech functions
  const handleSpeak = (textToRead) => {
    if (!textToRead.trim()) {
      toast.error("No text to read");
      return;
    }
    
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(textToRead);
      utterance.rate = profile.reading_speed / 200;
      utterance.onend = () => {
        setIsSpeaking(false);
        setIsPaused(false);
      };
      
      speechRef.current = utterance;
      window.speechSynthesis.speak(utterance);
      setIsSpeaking(true);
      setIsPaused(false);
    } else {
      toast.error("Text-to-speech is not supported in your browser");
    }
  };

  const handlePauseSpeech = () => {
    if (window.speechSynthesis.speaking) {
      if (isPaused) {
        window.speechSynthesis.resume();
        setIsPaused(false);
      } else {
        window.speechSynthesis.pause();
        setIsPaused(true);
      }
    }
  };

  const handleStopSpeech = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    setIsPaused(false);
  };

  const handleLogout = () => {
    handleStopSpeech();
    logout();
    navigate("/");
  };

  // Get display text based on what's available
  const displayText = summarizedText || text;

  // Text style based on profile
  const textStyle = {
    fontSize: `${profile.font_size}px`,
    fontFamily: profile.dyslexia_font_enabled ? 'Comic Sans MS, OpenDyslexic, sans-serif' : profile.font_family,
    lineHeight: profile.line_height,
    letterSpacing: `${profile.letter_spacing}em`,
    wordSpacing: `${profile.word_spacing}em`,
    backgroundColor: profile.background_color,
    color: profile.text_color,
  };

  const disorderProfiles = [
    { value: "general", label: "General" },
    { value: "dyslexia", label: "Dyslexia" },
    { value: "dysphagia", label: "Dysphagia" },
    { value: "vpd", label: "Visual Processing Disorder" },
    { value: "adhd", label: "ADHD" }
  ];

  const fontFamilies = [
    { value: "Open Sans", label: "Open Sans" },
    { value: "Arial", label: "Arial" },
    { value: "Georgia", label: "Georgia" },
    { value: "Verdana", label: "Verdana" },
    { value: "Tahoma", label: "Tahoma" }
  ];

  return (
    <div className="min-h-screen bg-[#F8F7FC]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-purple-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-1">
              <span className="text-xl font-bold text-[#1E3A8A]" style={{ fontFamily: 'Manrope, sans-serif' }}>READ</span>
              <span className="text-xl font-bold text-[#7C3AED]" style={{ fontFamily: 'Manrope, sans-serif' }}>BEST</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              {/* Settings Sheet */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="settings-btn">
                    <Settings className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                      <Settings className="w-5 h-5" />
                      Reading Settings
                    </SheetTitle>
                    <SheetDescription>
                      Customize your reading experience
                    </SheetDescription>
                  </SheetHeader>
                  
                  <div className="mt-6 space-y-6">
                    {/* Reading Profile */}
                    <div className="space-y-3">
                      <Label>Reading Profile</Label>
                      <Select 
                        value={profile.disorder_type} 
                        onValueChange={(value) => updateProfile({ disorder_type: value })}
                      >
                        <SelectTrigger data-testid="disorder-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {disorderProfiles.map((p) => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <Separator />

                    {/* Font Settings */}
                    <div className="space-y-4">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Type className="w-4 h-4" />
                        Typography
                      </h4>
                      
                      <div className="space-y-3">
                        <Label>Font Family</Label>
                        <Select 
                          value={profile.font_family} 
                          onValueChange={(value) => updateProfile({ font_family: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {fontFamilies.map((f) => (
                              <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-3">
                        <Label>Font Size: {profile.font_size}px</Label>
                        <Slider
                          value={[profile.font_size]}
                          onValueChange={([value]) => updateProfile({ font_size: value })}
                          min={12}
                          max={32}
                          step={1}
                          data-testid="font-size-slider"
                        />
                      </div>

                      <div className="space-y-3">
                        <Label>Line Height: {profile.line_height}</Label>
                        <Slider
                          value={[profile.line_height]}
                          onValueChange={([value]) => updateProfile({ line_height: value })}
                          min={1.2}
                          max={3}
                          step={0.1}
                        />
                      </div>

                      <div className="space-y-3">
                        <Label>Letter Spacing: {profile.letter_spacing}em</Label>
                        <Slider
                          value={[profile.letter_spacing]}
                          onValueChange={([value]) => updateProfile({ letter_spacing: value })}
                          min={0}
                          max={0.2}
                          step={0.01}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label htmlFor="dyslexia-font">Dyslexia-Friendly Font</Label>
                        <Switch
                          id="dyslexia-font"
                          checked={profile.dyslexia_font_enabled}
                          onCheckedChange={(checked) => updateProfile({ dyslexia_font_enabled: checked })}
                          data-testid="dyslexia-font-switch"
                        />
                      </div>
                    </div>

                    <Separator />

                    {/* Color Settings */}
                    <div className="space-y-4">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Colors
                      </h4>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Background</Label>
                          <div className="flex gap-2">
                            {["#F9F8F6", "#FFFEF0", "#F0FFF4", "#FFF0F5", "#1A202C"].map((color) => (
                              <button
                                key={color}
                                className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                                  profile.background_color === color ? "border-[#2563EB] scale-110" : "border-gray-200"
                                }`}
                                style={{ backgroundColor: color }}
                                onClick={() => updateProfile({ 
                                  background_color: color,
                                  text_color: color === "#1A202C" ? "#FFFFFF" : "#1A202C"
                                })}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* TTS Settings */}
                    <div className="space-y-4">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Volume2 className="w-4 h-4" />
                        Text-to-Speech
                      </h4>
                      
                      <div className="space-y-3">
                        <Label>Reading Speed: {profile.reading_speed} WPM</Label>
                        <Slider
                          value={[profile.reading_speed]}
                          onValueChange={([value]) => updateProfile({ reading_speed: value })}
                          min={100}
                          max={400}
                          step={10}
                        />
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>

              {/* User Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#1E3A8A] flex items-center justify-center text-white font-semibold">
                      {user?.name?.charAt(0).toUpperCase() || "U"}
                    </div>
                    <span className="hidden sm:inline text-[#1A202C]">{user?.name}</span>
                    <ChevronDown className="w-4 h-4 text-[#4A5568]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>{user?.email}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="flex items-center gap-2 text-red-600"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-purple-100 p-1 rounded-full">
            <TabsTrigger 
              value="read" 
              className="rounded-full px-6 data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white"
              data-testid="read-tab"
            >
              <AlignLeft className="w-4 h-4 mr-2" />
              Read
            </TabsTrigger>
            <TabsTrigger 
              value="saved" 
              className="rounded-full px-6 data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white"
              data-testid="saved-tab"
            >
              <FileText className="w-4 h-4 mr-2" />
              Saved Texts
            </TabsTrigger>
          </TabsList>

          {/* Read Tab */}
          <TabsContent value="read" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Input Section */}
              <Card className="border border-purple-100 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <FileText className="w-5 h-5 text-[#7C3AED]" />
                    Input Text
                  </CardTitle>
                  <CardDescription>
                    Paste or type the text you want to read
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    placeholder="Give your text a title (optional)"
                    value={textTitle}
                    onChange={(e) => setTextTitle(e.target.value)}
                    className="h-10"
                    data-testid="text-title-input"
                  />
                  <Textarea
                    placeholder="Paste your text here..."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    className="min-h-[300px] resize-none"
                    data-testid="text-input"
                  />
                  
                  <div className="flex flex-wrap gap-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-[#4A5568]">Summary Level:</Label>
                      <Select 
                        value={summarizationLevel} 
                        onValueChange={setSummarizationLevel}
                      >
                        <SelectTrigger className="w-32" data-testid="summary-level-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="brief">Brief</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="detailed">Detailed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <Button 
                      onClick={handleSummarize}
                      disabled={isSummarizing || !text.trim()}
                      className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-full"
                      data-testid="summarize-btn"
                    >
                      {isSummarizing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Summarizing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Summarize
                        </>
                      )}
                    </Button>
                    
                    <Button 
                      variant="outline"
                      onClick={handleSaveText}
                      disabled={!text.trim()}
                      className="rounded-full"
                      data-testid="save-text-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Reading Pane */}
              <Card className="border border-purple-100 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <BookOpen className="w-5 h-5 text-[#7C3AED]" />
                      Reading Pane
                    </CardTitle>
                    <CardDescription>
                      {summarizedText ? "Summarized text" : "Your text will appear here"}
                    </CardDescription>
                  </div>
                  
                  {/* TTS Controls */}
                  <div className="flex items-center gap-2">
                    {!isSpeaking ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSpeak(displayText)}
                        disabled={!displayText}
                        data-testid="play-speech-btn"
                      >
                        <Volume2 className="w-5 h-5" />
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={handlePauseSpeech}
                          data-testid="pause-speech-btn"
                        >
                          {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={handleStopSpeech}
                          data-testid="stop-speech-btn"
                        >
                          <Square className="w-5 h-5" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px] rounded-lg p-4" style={textStyle}>
                    {displayText ? (
                      <div className="reading-pane whitespace-pre-wrap" data-testid="reading-pane">
                        {displayText}
                      </div>
                    ) : (
                      <div className="text-center text-[#718096] py-12">
                        <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-30" />
                        <p>Enter text on the left to start reading</p>
                      </div>
                    )}
                  </ScrollArea>
                  
                  {summarizedText && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg text-sm text-green-700">
                      <Sparkles className="w-4 h-4 inline mr-2" />
                      Text has been summarized. Original: {text.split(' ').length} words → Summary: {summarizedText.split(' ').length} words
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Saved Texts Tab */}
          <TabsContent value="saved" className="space-y-6">
            <Card className="border border-purple-100 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <FileText className="w-5 h-5 text-[#7C3AED]" />
                  Saved Texts
                </CardTitle>
                <CardDescription>
                  Your saved texts for quick access
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingTexts ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" />
                  </div>
                ) : savedTexts.length === 0 ? (
                  <div className="text-center py-12 text-[#718096]">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No saved texts yet</p>
                    <p className="text-sm mt-2">Save some texts to access them here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {savedTexts.map((savedText) => (
                      <div 
                        key={savedText.id}
                        className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-[#1A202C] truncate">{savedText.title}</h4>
                            <p className="text-sm text-[#718096] mt-1 line-clamp-2">
                              {savedText.original_text.substring(0, 150)}...
                            </p>
                            <p className="text-xs text-[#A0AEC0] mt-2">
                              {new Date(savedText.created_at).toLocaleDateString()}
                              {savedText.summarized_text && (
                                <span className="ml-2 text-green-600">
                                  <Sparkles className="w-3 h-3 inline mr-1" />
                                  Summarized
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleLoadText(savedText)}
                              className="text-[#7C3AED]"
                              data-testid={`load-text-${savedText.id}`}
                            >
                              Open
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteText(savedText.id)}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              data-testid={`delete-text-${savedText.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default DashboardPage;
